import axios from 'axios';
import { client } from '@/services/api/axios.ts';
import { QueryClient, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

type TICreateFeedback = {
  plantIds: string[];
  userId: string;
};

async function assignPlantsToUser({
  plantIds,
  userId,
}: TICreateFeedback): Promise<object | undefined | void> {
  try {
    const postPromise = client.post(`/users/${userId}/plants`, {
      plantIds,
    });

    const { data } = await toast.promise(postPromise, {
      loading: 'Assigning plants to user...',
      success: 'Plants assigned to user successfully',
      error: (error) => {
        if (axios.isAxiosError(error)) {
          return error?.response?.data?.message || 'Failed to assign plants to user';
        }
        return error instanceof Error ? error.message : 'Failed to assign plants to user';
      },
    });

    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      toast.error(error?.response?.data?.message);
      throw new Error(`${error?.response?.data?.message}`);
    }

    if (error instanceof Error) {
      toast.error(error.message);
      throw new Error(`${error.message}}`);
    }
  }
}

export const useAssignPlantsToUser = ({
  queryClient,
  onSuccess,
  userId,
}: {
  onSuccess?: () => void;
  queryClient: QueryClient;
  userId: string;
}) => {
  return useMutation({
    mutationFn: assignPlantsToUser,
    onSuccess: () => {
      if (onSuccess) onSuccess();
    },
    onMutate: async ({ plantIds }) => {
      await queryClient.cancelQueries({ queryKey: ['users', userId, 'plants'] });

      const previousUserIds = queryClient.getQueryData(['users', userId, 'plants']);

      queryClient.setQueryData(['users', userId, 'plants'], () => ({
        plantIds,
      }));

      return { previousUserIds };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['users', userId, 'plants'] });
    },
  });
};
